import type { HttpInterceptorFn } from '@angular/common/http';
import { HttpErrorResponse } from '@angular/common/http';
import { retry, timer } from 'rxjs';

/**
 * Retry con backoff exponencial + jitter para GET idempotentes.
 *
 * Parte de la resiliencia HTTP de la cláusula 99,5%: reintenta errores de red y
 * 5xx transitorios, NUNCA 4xx (no tiene sentido reintentar un 403/404/422).
 * Sólo GET para no duplicar efectos secundarios (POST/PUT/DELETE quedan afuera).
 */
const MAX_RETRIES = 2;
const BASE_DELAY_MS = 400;

function isRetriable(error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse)) return false;
  // status 0 = red caída/CORS/timeout; 5xx = error transitorio de servidor.
  return error.status === 0 || (error.status >= 500 && error.status <= 599);
}

export const retryInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.method !== 'GET') return next(req);

  return next(req).pipe(
    retry({
      count: MAX_RETRIES,
      delay: (error, retryCount) => {
        if (!isRetriable(error)) throw error;
        // Backoff exponencial: 400ms, 800ms… + jitter [0..250ms).
        const backoff = BASE_DELAY_MS * 2 ** (retryCount - 1);
        const jitter = Math.random() * 250;
        return timer(backoff + jitter);
      },
    }),
  );
};
